import { useMemo, useState } from 'react'
import { Check, Slash } from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import Select from '../../../components/Select'
import DashboardTile from '../../../components/DashboardTile'
import Card from '../../../components/Card'
import SeasonSelect from '../../TeamDashboard/subpages/TeamLoot/components/SeasonSelect'
import { usePageInfo, useCharacterProgress } from '../../../config/queries'
import Difficulty, {
  difficultiesLabels,
} from 'app/frontend/types/custom/Difficulty'
import { useDifficulties } from 'app/frontend/base/hooks/useDifficulties'
import { cn } from '@/lib/utils'

interface Props {
  character?: wowaudit.Character
}

const RaidProgress = ({ character }: Props) => {
  const {
    data: { currentSeason, currentExpansion },
  } = usePageInfo()
  const { difficulties: availableDifficulties, defaultDifficulty } =
    useDifficulties()

  const { data: progress } = useCharacterProgress(character?.id)
  const isLoaded = progress !== undefined

  const [selectedSeasonId, setSelectedSeasonId] = useState(
    currentSeason.id.toString(),
  )
  const [selectedDifficulty, setSelectedDifficulty] = useState<
    Difficulty | undefined
  >()

  const selectedSeason = useMemo(
    () =>
      currentExpansion.seasons.find(
        (season) => season.id.toString() === selectedSeasonId,
      ) ?? currentSeason,
    [currentExpansion.seasons, selectedSeasonId, currentSeason],
  )

  const encounters = useMemo(() => {
    const instanceOrderById = new Map(
      selectedSeason.instances.map((instance) => [
        instance.id,
        instance.orderInSeason ?? 0,
      ]),
    )

    return selectedSeason.instances
      .flatMap((instance) => instance.encounters)
      .sort((a, b) => {
        const instanceOrder =
          (instanceOrderById.get(a.instanceId) ?? 0) -
          (instanceOrderById.get(b.instanceId) ?? 0)
        return (
          instanceOrder ||
          a.instanceId - b.instanceId ||
          a.defaultOrder - b.defaultOrder
        )
      })
  }, [selectedSeason])

  const autoDifficulty = useMemo<Difficulty>(() => {
    if (!progress) return defaultDifficulty
    return (
      availableDifficulties.find((difficulty) =>
        encounters.some(
          (encounter) =>
            (progress.raidProgression[difficulty]?.[encounter.id]?.kills ?? 0) >
            0,
        ),
      ) ?? defaultDifficulty
    )
  }, [progress, encounters, availableDifficulties, defaultDifficulty])

  const difficulty = selectedDifficulty ?? autoDifficulty
  const isCurrentSeason = selectedSeason.id === currentSeason.id

  return (
    <DashboardTile
      title={
        <span className='flex items-center gap-4'>
          Raid progress
          <Select
            className='h-6 text-sm font-normal'
            value={difficulty}
            setValue={(value) => setSelectedDifficulty(value as Difficulty)}
            options={availableDifficulties.map((value) => ({
              label: difficultiesLabels[value],
              value,
            }))}
          />
        </span>
      }
      size='medium'
      dataPresent={!!character}
      headerRight={
        <SeasonSelect
          className='w-50 h-6 font-normal text-sm'
          value={selectedSeasonId}
          setValue={(value) =>
            setSelectedSeasonId(value ?? currentSeason.id.toString())
          }
        />
      }
    >
      <div
        className='grid gap-2 pt-1'
        style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(6.5rem, 1fr))' }}
      >
        {encounters.map((encounter) => {
          const kills =
            progress?.raidProgression[difficulty]?.[encounter.id]?.kills ?? 0
          const killedThisWeek =
            progress?.weeklyRaidProgression[difficulty]?.[encounter.id]

          return (
            <Tooltip key={encounter.id}>
              <TooltipTrigger asChild>
                <Card
                  className={cn(
                    `flex items-center justify-between p-0 w-full ${
                      !isLoaded || !kills ? 'opacity-50 grayscale' : ''
                    }`,
                  )}
                >
                  <img
                    src={encounter.thumbnailUrl}
                    alt={encounter.name}
                    className='h-10 w-10 object-cover rounded-l-md border-r'
                  />
                  <div className='flex flex-col min-w-0 flex-1 px-2 py-0.5'>
                    <span className='truncate text-xs'>{encounter.name}</span>
                    <div
                      className={cn(
                        'flex items-center justify-between transition-opacity duration-300 gap-1.5',
                        isLoaded ? 'opacity-100' : 'opacity-0',
                      )}
                    >
                      <span
                        className={cn(
                          'text-base',
                          isLoaded && kills > 0
                            ? 'text-success-foreground'
                            : '',
                        )}
                      >
                        {kills}×
                      </span>
                      {isCurrentSeason ? (
                        <span>
                          {killedThisWeek ? (
                            <Check className='h-4 w-4 shrink-0 text-success-foreground' />
                          ) : (
                            <Slash className='h-3 w-3 shrink-0 text-muted-foreground/50' />
                          )}
                        </span>
                      ) : null}
                    </div>
                  </div>
                </Card>
              </TooltipTrigger>
              <TooltipContent className='flex flex-col gap-0.5 items-center'>
                <span>
                  {encounter.name} ({difficultiesLabels[difficulty]})
                </span>
                {isCurrentSeason ? (
                  killedThisWeek ? (
                    <span className='text-sm text-success-foreground'>
                      Killed this week
                    </span>
                  ) : (
                    <span className='text-sm text-muted-foreground italic'>
                      Not killed this week
                    </span>
                  )
                ) : null}
              </TooltipContent>
            </Tooltip>
          )
        })}
      </div>
    </DashboardTile>
  )
}

export default RaidProgress
